import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, IconButton, Badge, Tooltip, Popover, Typography, List, ListItem,
  ListItemText, Button, Divider, Chip, CircularProgress,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import api from '../services/api';

const LEVEL_COLOR = { critical: 'error', warning: 'warning', info: 'info' };
const REFRESH_MS  = 5 * 60 * 1000; // 5 minutes

export default function NotificationCenter({ sx }) {
  const navigate = useNavigate();
  const [anchorEl,      setAnchorEl]      = useState(null);
  const [alerts,        setAlerts]        = useState([]);
  const [brokerAlerts,  setBrokerAlerts]  = useState([]);
  const [loading,       setLoading]       = useState(false);
  const timerRef = useRef(null);

  const fetchAlerts = async () => {
    try {
      const [alertsRes, brokerRes] = await Promise.allSettled([
        api.get('/alerts'),
        api.get('/broker-alerts'),
      ]);
      if (alertsRes.status === 'fulfilled') {
        setAlerts(Array.isArray(alertsRes.value.data) ? alertsRes.value.data : []);
      }
      if (brokerRes.status === 'fulfilled') {
        setBrokerAlerts(brokerRes.value.data?.data || []);
      }
    } catch {
      // silent — bell still shows; badge falls back to 0
    }
  };

  useEffect(() => {
    fetchAlerts();
    timerRef.current = setInterval(fetchAlerts, REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, []);

  // Unread = unresolved DB alerts + active broker expiry alerts
  const unread = alerts.filter(a => !a.resolved_at && !a.read_at).length
               + brokerAlerts.filter(a => a.severity === 'critical').length;

  const handleOpen  = (e) => { setAnchorEl(e.currentTarget); fetchAlerts(); };
  const handleClose = ()  => setAnchorEl(null);

  const markResolved = async (id, e) => {
    e.stopPropagation();
    if (String(id).startsWith('broker-')) return; // broker alerts are computed — not resolvable here
    setLoading(true);
    try {
      await api.put(`/alerts/${id}/resolve`);
      setAlerts(prev => prev.filter(a => a.id !== id));
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const open = Boolean(anchorEl);

  // Merge DB alerts with broker expiry alerts for display
  const brokerAlertItems = brokerAlerts.map((a, i) => ({
    id:          `broker-${i}`,
    level:       a.severity,
    message:     a.message,
    triggered_at: a.due_date,
    action_url:  a.action_url,
    _isBrokerAlert: true,
  }));
  const dbAlerts = alerts.filter(a => !a.resolved_at).slice(0, 6);
  const recent = [...brokerAlertItems.filter(a => a.level === 'critical'), ...dbAlerts].slice(0, 8);

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          size="small"
          onClick={handleOpen}
          sx={{
            color: 'text.secondary',
            mr: 0.5,
            '&:hover': { color: 'text.primary', bgcolor: '#F7FAFC' },
            ...sx,
          }}
        >
          <Badge badgeContent={unread || null} color="error">
            <NotificationsIcon sx={{ fontSize: 20 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 4,
          sx: { width: 360, maxHeight: 480, borderRadius: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f0f4f8' }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Notifications
            {unread > 0 && (
              <Chip label={unread} color="error" size="small" sx={{ ml: 1, height: 18, fontSize: 11 }} />
            )}
          </Typography>
          <Button
            size="small"
            variant="text"
            onClick={() => { handleClose(); navigate('/notification-history'); }}
            sx={{ fontSize: 12, fontWeight: 600, color: '#1E6FD9', p: 0 }}
          >
            View All →
          </Button>
        </Box>

        {/* List */}
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!loading && recent.length === 0 && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <NotificationsIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">No active alerts</Typography>
            </Box>
          )}
          {!loading && (
            <List disablePadding>
              {recent.map((alert, i) => (
                <React.Fragment key={alert.id}>
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      py: 1.5,
                      px: 2,
                      bgcolor: !alert.read_at ? (
                        alert.level === 'critical' ? '#fff5f5' :
                        alert.level === 'warning'  ? '#fffbeb' : '#eff8ff'
                      ) : 'transparent',
                      '&:hover': { bgcolor: '#f8fafc' },
                    }}
                    secondaryAction={
                      <Tooltip title="Mark resolved">
                        <IconButton
                          size="small"
                          edge="end"
                          color="success"
                          onClick={(e) => markResolved(alert.id, e)}
                        >
                          <CheckCircleIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25, pr: 3 }}>
                          <Chip
                            label={alert.level}
                            color={LEVEL_COLOR[alert.level] || 'default'}
                            size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" component="span" sx={{ display: 'block', lineHeight: 1.4, pr: 3 }}>
                            {alert.message}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            {new Date(alert.triggered_at).toLocaleDateString('en-CA', {
                              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                            })}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {i < recent.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {/* Footer */}
        {recent.length > 0 && (
          <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid #f0f4f8', textAlign: 'center' }}>
            <Button
              size="small"
              fullWidth
              onClick={() => { handleClose(); navigate('/notification-history'); }}
              sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600 }}
            >
              View full notification history
            </Button>
          </Box>
        )}
      </Popover>
    </>
  );
}
